import type { PosSettings } from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  FormField,
  Input,
  MoneyInput,
  Select,
  SkeletonRows,
  StatusBadge,
  Textarea,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useCompanyStations } from "../../../features/accounting/shared.js";
import {
  type PosSettingsFormValues,
  posSettingsBody,
  posSettingsFormFrom,
} from "../../../features/pos/pos-values.js";
import { usePosPermissions, usePosSettings, useRefreshPos } from "../../../features/pos/queries.js";
import { PosPage, posSearchShape } from "../../../features/pos/shared.js";
import { api } from "../../../lib/api.js";
import { errorMessage, isApiError } from "../../../lib/errors.js";

const searchSchema = z.object({
  ...posSearchShape,
  /** "company" ya da bir istasyon kimliği: ayar satırı ŞİRKET ya da İSTASYON geçersiz kılmasıdır. */
  scope: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/_app/kasa/ayarlar")({
  validateSearch: (search) => searchSchema.parse(search),
  component: PosSettingsRoute,
});

/**
 * Kasa ayarları (Prompt 15). Satır ŞİRKET düzeyindedir; istasyon seçilirse o istasyona özel geçersiz kılma
 * yazılır (`shift_settings` / akaryakıt ayarlarıyla AYNI kalıp).
 *
 * Ekran OKUMAYA `pos.read` ile açılır; kaydetme `pos.manage` ister. Kayıt yoksa ürün varsayılanları
 * `isDefault` rozetiyle gösterilir ve hedef sürüm 0'dır (ilk PUT satırı oluşturur).
 */
function PosSettingsRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <PosPage
      title={t("menu.posSettings")}
      description={t("pos.settings.description")}
      breadcrumb={[{ label: t("menu.posSettings") }]}
    >
      {({ companyId }) => <SettingsBody companyId={companyId} />}
    </PosPage>
  );
}

function SettingsBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const stations = useCompanyStations(companyId);
  const scope = search.scope ?? "company";
  const scopeStationId = scope === "company" ? null : scope;
  const settings = usePosSettings(companyId, scopeStationId);

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <FormField label={t("pos.settings.scope")} description={t("pos.settings.scopeHint")} className="w-72">
          {(control) => (
            <Select
              {...control}
              value={scope}
              onChange={(event) =>
                void navigate({
                  search: (previous) => ({ ...previous, scope: event.target.value }),
                  replace: true,
                })
              }
            >
              <option value="company">{t("pos.settings.companyScope")}</option>
              {(stations.data ?? []).map((station) => (
                <option key={station.id} value={station.id}>
                  {station.code} {station.name}
                </option>
              ))}
            </Select>
          )}
        </FormField>
      </Card>

      {settings.error ? (
        <Alert tone="danger">{errorMessage(settings.error)}</Alert>
      ) : !settings.data ? (
        <SkeletonRows rows={6} />
      ) : (
        <SettingsForm
          key={`${companyId}:${scope}`}
          settings={settings.data as PosSettings}
          scopeStationId={scopeStationId}
          onReload={() => void settings.refetch()}
        />
      )}
    </>
  );
}

function SettingsForm({
  settings,
  scopeStationId,
  onReload,
}: {
  settings: PosSettings;
  scopeStationId: string | null;
  onReload: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshPos();
  const canManage = usePosPermissions().canManage;
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);

  // İstasyon kapsamında henüz geçersiz kılma yoksa şirket satırının değerleri gösterilir; hedef sürüm 0'dır.
  const overrideExists = scopeStationId !== null && settings.stationId === scopeStationId;
  const isDefaultRow = scopeStationId === null ? settings.isDefault : !overrideExists;
  const targetVersion = scopeStationId === null ? settings.version : overrideExists ? settings.version : 0;

  const [values, setValues] = useState<PosSettingsFormValues>(() =>
    posSettingsFormFrom(settings, { stationId: scopeStationId, version: targetVersion }),
  );
  useEffect(() => {
    setValues(posSettingsFormFrom(settings, { stationId: scopeStationId, version: targetVersion }));
  }, [settings, scopeStationId, targetVersion]);

  const patch = (changes: Partial<PosSettingsFormValues>) =>
    setValues((current) => ({ ...current, ...changes }));

  const save = useMutation({
    mutationFn: async (body: PosSettingsFormValues) =>
      (await api.PUT("/pos/settings", { body: posSettingsBody(body) as never })).data!,
    onSuccess: async (saved) => {
      await refresh();
      setValues(
        posSettingsFormFrom(saved as PosSettings, {
          stationId: scopeStationId,
          version: (saved as PosSettings).version,
        }),
      );
      toast({ tone: "success", title: t("pos.settings.saved") });
    },
    onError: (caught) => {
      if (isApiError(caught, "VERSION_CONFLICT")) {
        setConflict(true);
        return;
      }
      setError(errorMessage(caught));
    },
  });

  return (
    <form
      className="grid gap-4"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        setError(null);
        setConflict(false);
        save.mutate(values);
      }}
    >
      {conflict ? (
        <Alert tone="warning" title={t("pos.settings.conflictTitle")}>
          <div className="grid gap-2">
            <p>{t("pos.settings.conflictBody")}</p>
            <div>
              <Button variant="outline" size="sm" onClick={onReload}>
                {t("pos.settings.reload")}
              </Button>
            </div>
          </div>
        </Alert>
      ) : null}
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {canManage ? null : <Alert tone="info">{t("pos.settings.readOnly")}</Alert>}
      {isDefaultRow ? (
        <Alert tone="info" title={t("pos.settings.isDefaultTitle")}>
          {scopeStationId === null ? t("pos.settings.isDefaultBody") : t("pos.settings.noOverrideBody")}
        </Alert>
      ) : null}
      {values.needsVerification ? (
        <Alert tone="warning" title={t("pos.settings.needsVerificationTitle")}>
          <div className="grid gap-2">
            <p>{t("pos.settings.needsVerificationBody")}</p>
            {canManage ? (
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  loading={save.isPending}
                  onClick={() => {
                    // Durum güncellemesi ASENKRONDUR: gövde o anki değerlerden kurulur, `values` beklenmez.
                    const verified = { ...values, needsVerification: false };
                    setValues(verified);
                    save.mutate(verified);
                  }}
                >
                  {t("pos.settings.markVerified")}
                </Button>
              </div>
            ) : null}
          </div>
        </Alert>
      ) : (
        <p>
          <StatusBadge tone="success">{t("pos.settings.verified")}</StatusBadge>
        </p>
      )}

      <Card className="grid gap-4 p-4 sm:p-5">
        <h2 className="text-base font-semibold">{t("pos.settings.sections.rules")}</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="flex min-h-6 items-start gap-2 text-sm sm:col-span-2">
            <Checkbox
              className="mt-1"
              checked={values.fiscalRequired}
              disabled={!canManage}
              onChange={(event) => patch({ fiscalRequired: event.target.checked })}
            />
            <span className="grid gap-0.5">
              <span className="font-medium">{t("pos.settings.fiscalRequired")}</span>
              <span className="text-xs text-muted-foreground">{t("pos.settings.fiscalRequiredHint")}</span>
            </span>
          </label>

          <FormField
            label={t("pos.settings.offlineCreditTolerance")}
            description={t("pos.settings.offlineCreditToleranceHint")}
          >
            {(control) => (
              <MoneyInput
                {...control}
                value={values.offlineCreditTolerance}
                disabled={!canManage}
                onValueChange={(minor) => patch({ offlineCreditTolerance: minor })}
              />
            )}
          </FormField>

          <FormField
            label={t("pos.settings.pendingFuelAutoSettleMinutes")}
            description={t("pos.settings.pendingFuelAutoSettleMinutesHint")}
          >
            {(control) => (
              <Input
                {...control}
                type="number"
                inputMode="numeric"
                min={0}
                max={1440}
                disabled={!canManage}
                value={values.pendingFuelAutoSettleMinutes}
                onChange={(event) => patch({ pendingFuelAutoSettleMinutes: event.target.value })}
              />
            )}
          </FormField>

          <label className="flex min-h-6 items-start gap-2 text-sm sm:col-span-2">
            <Checkbox
              className="mt-1"
              checked={values.ageRestrictedConfirmation}
              disabled={!canManage}
              onChange={(event) => patch({ ageRestrictedConfirmation: event.target.checked })}
            />
            <span className="grid gap-0.5">
              <span className="font-medium">{t("pos.settings.ageRestrictedConfirmation")}</span>
              <span className="text-xs text-muted-foreground">
                {t("pos.settings.ageRestrictedConfirmationHint")}
              </span>
            </span>
          </label>

          <FormField
            label={t("pos.settings.receiptFooter")}
            description={t("pos.settings.receiptFooterHint")}
            className="sm:col-span-2"
          >
            {(control) => (
              <Textarea
                {...control}
                rows={3}
                maxLength={300}
                disabled={!canManage}
                value={values.receiptFooter}
                onChange={(event) => patch({ receiptFooter: event.target.value })}
              />
            )}
          </FormField>
        </div>
      </Card>

      {canManage ? (
        <div className="flex justify-end">
          <Button type="submit" loading={save.isPending}>
            {t("common.save")}
          </Button>
        </div>
      ) : null}
    </form>
  );
}
